'use client';

import { useCallback, useEffect, useRef } from 'react';
import { PublicKey } from '@solana/web3.js';
import { useCustomWalletAutoconnect } from 'src/hooks/useCustomWalletAutoconnect';
import useWalletContext from 'src/hooks/useWalletContext';
import { dlog } from '../dev';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useCurrentWalletConnectionState from './useCurrentWalletConnectionState';
import useDriftClient from './useDriftClient';
import { useLinkWallet } from './wallet-management/useLinkWallet';
import useWalletDisconnect from './wallet-management/useWalletDisconnect';
import { LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY } from 'src/hooks/useWalletsToDisplay';

/**
 * A hook which handles making sure the app correctly reacts to the wallet changing state, keeps the store state in sync with the changing wallet, and attaches the wallet's events to the assosciated methods in the store
 *
 * Note : It's OK that magic wallet isn't included in the useWallet hook because that is handled seperately (in driftStoreActions, on connect)
 */
const useKeepWalletsInSync = () => {
	const setState = useDriftStore((s) => s.set);
	useCustomWalletAutoconnect();
	const walletContext = useWalletContext();
	const authority = walletContext?.wallet?.adapter?.publicKey;
	const driftClient = useDriftClient();
	const isEmulatingAccount = useDriftStore((s) => s.isEmulatingAccount);
	const walletConnectionState = useCurrentWalletConnectionState();

	const { linkWalletToDriftClient } = useLinkWallet();
	const { handleWalletDisconnect } = useWalletDisconnect();

	const wallets = walletContext.wallets;

	const currentWalletState = useDriftStore((s) => s.wallet);

	const alreadySyncedAuthority = useRef<PublicKey>(null);

	const handleDisconnect = useCallback(() => {
		dlog(`wallet_debugging`, `useKeepWalletsInSync::handle_disconnect`);

		alreadySyncedAuthority.current = null;
		handleWalletDisconnect();
		walletConnectionState.instance.update('NotConnected', true);
	}, [handleWalletDisconnect, walletConnectionState.instance]);

	/**
	 * This method is designed to handle the race-condition where the wallet might want to connect before the RPC-Connection or DriftClient are actually ready to go. It first checks if they are ready, otherwise sets up a handler to handle the wallet connection once they are ready to go.
	 */

	// Keep wallet adapters in sync
	useEffect(() => {
		setState((s) => {
			s.wallets = wallets;
		});
	}, [wallets]);

	// TODO :: The fact that we need to set these handlers up seperately is weird and annoying. I think the issue is that lots of our wallet code assumes the current wallet is inside of WalletContext when IT IS NOT - ONLY solana/react WALLETS are in here. So I think that metamask/magic walleta are also not behaving correctly until we standardise this.
	// Add Custom Wallet event handlers
	useEffect(() => {
		if (!currentWalletState?.current?.adapter) return;

		if (
			!currentWalletState.isAppKit &&
			!currentWalletState.isMagicAuth &&
			!currentWalletState.isPrivyNativeAuth
		) {
			return;
		}

		currentWalletState.current.adapter.on('disconnect', handleDisconnect);

		return () => {
			currentWalletState.current.adapter?.removeListener(
				'disconnect',
				handleDisconnect
			);
		};
	}, [currentWalletState, handleDisconnect]);

	// Set up solana/react wallet event handlers
	useEffect(() => {
		const wallet = walletContext?.wallet;

		if (!wallet) return;

		setState((s) => {
			// @ts-expect-error
			s.wallet.current = wallet;
		});

		wallet.adapter.on('disconnect', handleDisconnect);

		return () => {
			wallet.adapter.removeListener('disconnect', handleDisconnect);
		};
	}, [
		walletContext?.wallet?.adapter,
		handleDisconnect,
		currentWalletState.current.adapter?.name,
		currentWalletState.isPrivyNativeAuth,
	]);

	// Ensure that wallet connection logic happens when the adapter connects
	useEffect(() => {
		// Exit if we're using account emulation
		if (isEmulatingAccount) {
			return;
		}

		if (authority) {
			// Skip doing connection logic if we've already connected for this wallet
			if (alreadySyncedAuthority?.current?.equals(authority)) {
				return;
			}

			if (driftClient) {
				alreadySyncedAuthority.current = authority;
				linkWalletToDriftClient();
			}

			if (currentWalletState.current.adapter) {
				localStorage.setItem(
					LAST_USED_WALLET_NAME_LOCALSTORAGE_KEY,
					currentWalletState.current.adapter.name
				);
			}
		}
	}, [
		walletConnectionState,
		authority,
		isEmulatingAccount,
		driftClient,
		linkWalletToDriftClient,
		currentWalletState?.current?.adapter?.name,
	]);

	// Store wallet context on the window for e2e tests
	useEffect(() => {
		// @ts-ignore
		window.walletContext = walletContext;
	}, [walletContext]);
};

export default useKeepWalletsInSync;
